<script setup>
import getUuid from 'widget/helpers/uuid';
import { ref, computed, onMounted, onUnmounted } from 'vue';
import WaveSurfer from 'wavesurfer.js';
import RecordPlugin from 'wavesurfer.js/dist/plugins/record.js';
import { format, intervalToDuration } from 'date-fns';
import { convertAudio } from './utils/audioConversionUtils';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  audioRecordFormat: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(['finishRecord', 'recordError', 'pauseStateChange']);

const waveformContainer = ref(null);
const wavesurfer = ref(null);
const record = ref(null);
const isPaused = ref(false);
const isPlaying = ref(false);
const previewUrl = ref(null);
const elapsedTime = ref(0);
const recordedDuration = ref(0);

const formatTimeProgress = time => {
  const duration = intervalToDuration({ start: 0, end: time });
  return format(
    new Date(0, 0, 0, 0, duration.minutes, duration.seconds),
    'mm:ss'
  );
};

const durationText = computed(() => formatTimeProgress(elapsedTime.value));

const AUDIO_EXTENSION_MAP = {
  'audio/ogg': 'ogg',
  'audio/mp3': 'mp3',
  'audio/mpeg': 'mp3',
  'audio/wav': 'wav',
  'audio/webm': 'webm',
};

const getRecordPluginOptions = audioFormat => {
  const options = {
    scrollingWaveform: true,
    renderRecordedAudio: false,
  };
  if (
    audioFormat === 'audio/ogg' &&
    MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')
  ) {
    options.mimeType = 'audio/ogg;codecs=opus';
  }
  return options;
};

const initWaveSurfer = () => {
  wavesurfer.value = WaveSurfer.create({
    container: waveformContainer.value,
    waveColor: '#1F93FF',
    progressColor: '#6E6F73',
    height: 32,
    barWidth: 2,
    barGap: 2,
    barRadius: 2,
    cursorWidth: 0,
    plugins: [
      RecordPlugin.create(getRecordPluginOptions(props.audioRecordFormat)),
    ],
  });

  wavesurfer.value.on('play', () => {
    isPlaying.value = true;
  });
  wavesurfer.value.on('pause', () => {
    isPlaying.value = false;
  });
  wavesurfer.value.on('finish', () => {
    isPlaying.value = false;
    elapsedTime.value = recordedDuration.value;
  });
  wavesurfer.value.on('timeupdate', time => {
    if (isPlaying.value) elapsedTime.value = time * 1000;
  });

  record.value = wavesurfer.value.plugins[0];

  // Pausing hands us everything recorded so far, so it can be reviewed before
  // resuming or sending.
  record.value.on('record-pause', blob => {
    if (previewUrl.value) URL.revokeObjectURL(previewUrl.value);
    previewUrl.value = URL.createObjectURL(blob);
    recordedDuration.value = elapsedTime.value;
    wavesurfer.value.setOptions({ interact: true });
    wavesurfer.value.load(previewUrl.value);
  });

  record.value.on('record-end', async blob => {
    try {
      const audioBlob = await convertAudio(blob, props.audioRecordFormat);
      // Use the converted blob's actual type, which may differ from the
      // requested format when the browser can't produce it (e.g. Safari falls
      // back to MP3 instead of OGG). This keeps the filename, content type, and
      // voice-note flag consistent with the real bytes.
      const audioType = audioBlob.type || props.audioRecordFormat;
      const ext = AUDIO_EXTENSION_MAP[audioType] || 'mp3';
      const fileName = `${getUuid()}.${ext}`;
      const file = new File([audioBlob], fileName, {
        type: audioType,
      });
      emit('finishRecord', {
        name: file.name,
        type: file.type,
        size: file.size,
        file,
      });
    } catch (error) {
      emit('recordError', { error });
    }
  });

  record.value.on('record-progress', time => {
    elapsedTime.value = time;
  });
};

const stopRecording = () => {
  record.value.stopRecording();
};

const pauseRecording = () => {
  record.value.pauseRecording();
  isPaused.value = true;
  emit('pauseStateChange', true);
};

const resumeRecording = () => {
  if (isPlaying.value) wavesurfer.value.pause();
  wavesurfer.value.setOptions({ interact: false });
  record.value.resumeRecording();
  isPaused.value = false;
  emit('pauseStateChange', false);
};

const togglePlayback = () => {
  wavesurfer.value.playPause();
};

onMounted(() => {
  initWaveSurfer();
  record.value.startRecording();
});

onUnmounted(() => {
  if (previewUrl.value) {
    URL.revokeObjectURL(previewUrl.value);
    previewUrl.value = null;
  }
  if (wavesurfer.value) {
    wavesurfer.value.destroy();
  }
});

defineExpose({ stopRecording, pauseRecording, resumeRecording });
</script>

<template>
  <div class="flex items-center w-full gap-3 py-2">
    <button
      v-if="isPaused"
      class="grid p-0 bg-transparent border-0 shrink-0 size-6 place-content-center text-n-slate-12"
      :aria-label="
        isPlaying
          ? $t('CONVERSATION.AUDIO_PLAYER.PAUSE')
          : $t('CONVERSATION.AUDIO_PLAYER.PLAY')
      "
      @click="togglePlayback"
    >
      <Icon
        class="size-5"
        :icon="isPlaying ? 'i-ph-pause-fill' : 'i-ph-play-fill'"
      />
    </button>
    <span
      v-else
      class="rounded-full shrink-0 size-2 bg-n-ruby-9 animate-pulse"
    />
    <span class="text-sm tabular-nums shrink-0 text-n-slate-11">
      {{ durationText }}
    </span>
    <div ref="waveformContainer" class="flex-1 min-w-0" />
  </div>
</template>
